import cn, { Argument as ClassName } from 'classnames'
import React, { memo, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useSearchParams } from 'react-router-dom'
import { pickBy } from 'lodash'

import { SortOrders } from '../../../common/constants/SortOrders'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import chargePointHasPool from '../../../lib/chargePointHasPool'
import { ChargepointStatus } from '../../../lib/chargepoint-status'
import { ChargersPagination } from '../../../lib/chargers-pagination'
import { PageLayoutContainer } from '../../../lib/page-layout-container'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../../../lib/table'
import SettingsInfo from '../../../ui/chargepoints/SettingsInfo'
import { AddChargePointModal } from '../../components/AddChargePointModal'
import { ImportChargePointModal } from '../../components/ImportChargePointModal'
import SortedHeader from '../../components/SortedHeader'
import { RootState } from '../../redux/store'
import { downloadBlob } from '../../services/BlobService'
import { WidgetLayout } from '../../../lib/widget-layout'
import { ChargePointsSearchWidget } from '../../../support/features/charge-points-search-widget'
import ListPageHeader from '../../../common/features/ListPageHader/ListPageHeader'
import {
    ChargePoint,
    useCompanyGetChargePointsPagedQuery,
    useCompanyRemoveChargePointMutation,
    useLazyCompanyCreateImportStringQuery,
} from '../../../common/redux/api/web-api'
import { DropdownMenuItem } from '../../../common/components/DropdownMenu'
import AddIcon from '../../../lib/icons/add-icon/add-icon'
import ImportIcon from '../../../lib/icons/import-icon/import-icon'
import DownloadIcon from '../../../lib/icons/download-icon/download-icon'
import { ChargePointPageFilterStatus } from '../../../support/types/ChargePointsPageFilterStatus'
import { getOwner } from '../../../common/utils/chargePointUtils'
import localizeDate from '../../../common/utils/localizeDate'
import useText from '../../../common/hooks/useText'

export type AdminChargepointsPageProps = {
    className?: ClassName
    style?: React.CSSProperties
}

const mapSearchParamsToFilter = ({
    id,
    imsiNumber,
    companyId,
    status,
    name,
    ownerEmail,
    ownerName,
    ownerMobile,
    ocppOnly,
    productId,
}: Partial<ChargePointPageFilterStatus>): Partial<ChargePointPageFilterStatus> => {
    return {
        id,
        imsiNumber,
        companyId,
        status,
        name,
        ownerEmail,
        ownerName,
        ownerMobile,
        ocppOnly,
        productId,
    }
}

enum SortParameter {
    ID = 'id',
    NAME = 'name',
    STATUS = 'chargePointStatus',
    ORGANISATION = 'companyName',
    LAST_HEARTBEAT = 'lastHeartbeat',
    OWNER_NAME = 'ownerName',
    DESCRIPTION = 'additionalInfo.description',
    LOADBALANCER_GROUP_NAME = 'loadBalancerGroupName',
}

export const AdminChargePointsPage = memo(function AdminChargepointsPage({
    className,
    style,
}: AdminChargepointsPageProps): React.ReactElement {
    const text = useText()
    const [chargePointModalOpen, setChargePointModalOpen] = useState(false)
    const [currentPage, setCurrentPage] = useState(0)
    const [importChargePointModalOpen, setImportChargePointModalOpen] = useState(false)
    const [pageSize, setPageSize] = useState(20)
    const [sortFilter, setSortFilter] = useState<{ parameter: SortParameter; reversed: boolean }>({
        parameter: SortParameter.ID,
        reversed: false,
    })

    const [searchParams, setParams] = useSearchParams()
    const params = Object.fromEntries(searchParams.entries())
    const filter = mapSearchParamsToFilter(params)

    const { isAdmin, selectedCompanyId: companyId } = useSelector((state: RootState) => state.user)

    const {
        data: chargePoints,
        error,
        isLoading,
        refetch,
    } = useCompanyGetChargePointsPagedQuery(
        {
            id: filter.id,
            CompanyId: companyId,
            companyId: companyId,
            sortOrder: sortFilter.reversed ? SortOrders.Descending : SortOrders.Ascending,
            column: sortFilter.parameter,
            page: currentPage,
            noOfItems: pageSize,
            status: filter.status,
            productId: filter.productId,
            name: filter.name,
            imsiNumber: filter.imsiNumber,
            ownerName: filter.ownerName,
            ownerEmail: filter.ownerEmail,
            ownerMobile: filter.ownerMobile,
            ocppOnly: filter.ocppOnly,
            expand: 'additionalInfo,ocppConfig',
        },
        { refetchOnMountOrArgChange: true }
    )

    const [removeChargePointMutation] = useCompanyRemoveChargePointMutation()
    const [trigger] = useLazyCompanyCreateImportStringQuery()

    useEffect(() => {
        if (error) notifyError(error.data ?? text('msgChargersFailedToFetch'))
    }, [error])

    const downloadImportInfo = async () => {
        try {
            notifySuccess(text('msgDownloadImportInformationStarted'))

            const response = await trigger({ companyId: companyId })

            if (!response || !response.data) throw Error

            downloadBlob(response.data, text('lblImportDataCSVFile'))
        } catch (e) {
            notifyError(text('msgDownloadImportInformationFailed'))
        }
    }

    const openAddChargePoint = () => {
        setChargePointModalOpen(true)
    }

    const openImportChargePoint = () => {
        setImportChargePointModalOpen(true)
    }

    const removeChargePoint = async (chargePoint: ChargePoint) => {
        try {
            if (confirm(text('msgConfirmChargerRemoval'))) {
                await removeChargePointMutation({
                    companyId: companyId,
                    chargePointId: chargePoint.id,
                }).unwrap()

                refetch()
            }
        } catch (e: unknown) {
            notifyError(text('msgChargerFailedToRemove'))
        }
    }

    const saveAndClose = () => {
        notifySuccess(text('lblChargerWasAdded'))

        refetch()

        setChargePointModalOpen(false)
    }

    const applySortFilter = (sortParamName: SortParameter) => {
        setSortFilter({
            parameter: sortParamName,
            reversed: sortFilter.parameter === sortParamName ? !sortFilter?.reversed : false,
        })
    }

    const filterSubmit = (filter: Partial<ChargePointPageFilterStatus>) => {
        setParams(pickBy(filter, (value) => value !== undefined && value !== null))
        if (currentPage !== 0) setCurrentPage(0)
    }

    return (
        <>
            <PageLayoutContainer className={cn(className)} style={style}>
                <WidgetLayout
                    data-testid="table-charge-points"
                    className={cn('flex-1', className)}
                    style={style}
                    headerButtons={<ChargePointsSearchWidget onSubmit={filterSubmit} filter={filter} />}
                    contextMenuItems={
                        <>
                            <DropdownMenuItem
                                onClick={() => openAddChargePoint()}
                                text={text('btnAddCharger')}
                                icon={<AddIcon />}
                                key="btnAddCharger"
                                data-testid="open-add-chargepoints-btn"
                            />
                            <DropdownMenuItem
                                onClick={() => openImportChargePoint()}
                                text={text('lblChargersImportModal')}
                                icon={<ImportIcon />}
                                key="btnImportChargers"
                                data-testid="open-import-chargepoints-btn"
                            />
                            <DropdownMenuItem
                                onClick={() => downloadImportInfo()}
                                text={text('msgDownloadImportInformation')}
                                icon={<DownloadIcon />}
                                key="btnDownloadImportInformation"
                                data-testid="open-download-import-info-btn"
                            />
                        </>
                    }
                >
                    <ListPageHeader
                        amount={chargePoints?.totalCount}
                        isLoading={isLoading}
                        pageSize={pageSize}
                        setCurrentPage={setCurrentPage}
                        setPageSize={setPageSize}
                    />
                    <Table className="flex-1" isLoading={isLoading} data-testid="chargepoints-table">
                        <TableHeader>
                            <TableRow>
                                <SortedHeader
                                    filter={sortFilter}
                                    sortType="id"
                                    text={text('lblSerialNumberShort')}
                                    setSortParams={() => {
                                        applySortFilter(SortParameter.ID)
                                    }}
                                />
                                <SortedHeader
                                    filter={sortFilter}
                                    sortType="chargePointStatus"
                                    text={text('lblStatus')}
                                    setSortParams={() => {
                                        applySortFilter(SortParameter.STATUS)
                                    }}
                                />
                                <TableHeaderCell className="heading5">{text('lblSettings')}</TableHeaderCell>
                                <SortedHeader
                                    filter={sortFilter}
                                    sortType="lastHeartbeat"
                                    text={text('lblOffline')}
                                    setSortParams={() => {
                                        applySortFilter(SortParameter.LAST_HEARTBEAT)
                                    }}
                                />
                                <SortedHeader
                                    filter={sortFilter}
                                    sortType="name"
                                    text={text('lblName')}
                                    setSortParams={() => {
                                        applySortFilter(SortParameter.NAME)
                                    }}
                                />
                                <SortedHeader
                                    filter={sortFilter}
                                    sortType="ownerName"
                                    text={text('lblUserOrOwner')}
                                    setSortParams={() => {
                                        applySortFilter(SortParameter.OWNER_NAME)
                                    }}
                                />
                                <SortedHeader
                                    filter={sortFilter}
                                    sortType="additionalInfo.description"
                                    text={text('lblDescription')}
                                    setSortParams={() => {
                                        applySortFilter(SortParameter.DESCRIPTION)
                                    }}
                                />
                                <SortedHeader
                                    filter={sortFilter}
                                    sortType="loadBalancerGroupName"
                                    text={text('lblLoadBalancer')}
                                    setSortParams={() => {
                                        applySortFilter(SortParameter.LOADBALANCER_GROUP_NAME)
                                    }}
                                />
                                <TableHeaderCell className="heading5 hidden-xs hidden-sm">
                                    {text('lblPool')}
                                </TableHeaderCell>
                                <TableHeaderCell className="heading5"></TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody isLoading={isLoading} numberOfLoadingColumns={9}>
                            {chargePoints &&
                                chargePoints.items?.map((chargePoint: ChargePoint) => (
                                    <TableRow key={chargePoint.id} data-testid={`chargepoint-${chargePoint.id}`}>
                                        <TableBodyCell>
                                            <Link className="table btn-link" to={`/chargepoints/${chargePoint.id}`}>
                                                {chargePoint.id}
                                            </Link>
                                        </TableBodyCell>
                                        <TableBodyCell>
                                            <ChargepointStatus chargePoint={chargePoint} />
                                        </TableBodyCell>
                                        <TableBodyCell>{SettingsInfo({ chargePoint })}</TableBodyCell>
                                        <TableBodyCell>
                                            {chargePoint.chargePointStatus == 'Offline' && (
                                                <span>
                                                    {localizeDate(
                                                        chargePoint.isOcpp
                                                            ? chargePoint.offlineSince
                                                            : chargePoint.lastHeartbeat
                                                    )}
                                                </span>
                                            )}
                                        </TableBodyCell>
                                        <TableBodyCell>{chargePoint.name}</TableBodyCell>
                                        <TableBodyCell className="hidden-xs hidden-sm">
                                            {getOwner(chargePoint)}
                                        </TableBodyCell>
                                        <TableBodyCell className="hidden-xs hidden-sm">
                                            {chargePoint.additionalInfo?.description}
                                        </TableBodyCell>
                                        <TableBodyCell className="hidden-xs hidden-sm">
                                            <Link to={`/loadbalancer/${chargePoint.loadBalancerGroupId}`}>
                                                {chargePoint.loadBalancerGroupName}
                                            </Link>
                                        </TableBodyCell>
                                        <TableBodyCell className="hidden-xs hidden-sm">
                                            {chargePointHasPool(chargePoint) && chargePoint.poolName}
                                        </TableBodyCell>
                                        <TableBodyCell>
                                            {isAdmin && (
                                                <button
                                                    type="button"
                                                    data-toggle="tooltip"
                                                    data-placement="top"
                                                    title={text('btnRemoveWithLBNote')}
                                                    onClick={() => removeChargePoint(chargePoint)}
                                                    className="btn btn-danger btn-xs btn-icon"
                                                    disabled={!!chargePoint.loadBalancerGroupId}
                                                >
                                                    <span className="fa fa-remove"></span>
                                                </button>
                                            )}
                                        </TableBodyCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                    {!isLoading && chargePoints && (
                        <ChargersPagination
                            totalCount={chargePoints?.totalCount ?? 0}
                            pageIndex={filter?.page ?? 0}
                            setPageIndex={setCurrentPage}
                            pageSize={chargePoints?.pageSize ?? 0}
                            displayedChargers={chargePoints?.items?.length ?? 0}
                        />
                    )}
                </WidgetLayout>
            </PageLayoutContainer>
            {chargePointModalOpen && (
                <AddChargePointModal close={saveAndClose} cancel={() => setChargePointModalOpen(false)} />
            )}
            {importChargePointModalOpen && (
                <ImportChargePointModal
                    close={() => {
                        setImportChargePointModalOpen(false)
                        notifySuccess(text('lblChargerWasAdded'))
                        refetch()
                    }}
                    cancel={() => setImportChargePointModalOpen(false)}
                />
            )}
        </>
    )
})
